import React, { useState, useEffect } from 'react';
import { WindowElement } from '../types';

interface WindowDimensionPromptProps {
  isOpen: boolean;
  windowElement: WindowElement | null;
  onSave: (dimensions: { 
    width: number; 
    height: number; 
    trickleVents: number; 
    isDraughtProofed: boolean; 
    windowType?: 'pvc' | 'wood' | 'metal';
    orientation?: string;
    glazingGap?: string;
    glazingType?: string;
    windowCategory?: string;
    condition?: 'good' | 'average' | 'poor';
  }) => void;
  onDelete: () => void;
  onClose: () => void;
  isFullscreen?: boolean;
}

const WindowDimensionPrompt: React.FC<WindowDimensionPromptProps> = ({ isOpen, windowElement, onSave, onDelete, onClose, isFullscreen = false }) => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [trickleVents, setTrickleVents] = useState(0);
  const [isDraughtProofed, setIsDraughtProofed] = useState(false);
  const [windowType, setWindowType] = useState<'pvc' | 'wood' | 'metal' | ''>('');
  const [selectedOrientation, setSelectedOrientation] = useState<string>('');
  
  // New glazing options
  const [glazingGap, setGlazingGap] = useState<string>('');
  const [glazingType, setGlazingType] = useState<string>('');
  const [windowCategory, setWindowCategory] = useState<string>('');
  const [condition, setCondition] = useState<'good' | 'average' | 'poor' | ''>('');

  // Available options
  const orientations = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  
  const glazingGapOptions = [
    '6 mm',
    '12 mm',
    '16 mm or more'
  ];

  const glazingTypeOptions = [
    'Single glazing',
    'Double pre 2002',
    'Double between 2002 and 2021',
    'Double post or during 2022',
    'Double with unknown install date',
    'Secondary glazing',
    'Triple pre 2002',
    'Triple between 2002 and 2021',
    'Triple post or during 2022',
    'Triple with unknown install date',
    'Single glazing, known data',
    'Double glazing, known data',
    'Triple glazing, known data'
  ];

  const windowCategoryOptions = [
    'Window',
    'Roof window'
  ];

  // Format input to ensure proper decimal formatting
  const formatDecimalInput = (value: string): string => {
    if (!value) return '';
    
    // Remove any non-numeric characters except decimal point
    let cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Handle multiple decimal points - keep only the first one
    const decimalIndex = cleanValue.indexOf('.');
    if (decimalIndex !== -1) {
      cleanValue = cleanValue.substring(0, decimalIndex + 1) + cleanValue.substring(decimalIndex + 1).replace(/\./g, '');
    }
    
    // Auto-add decimal point for common input patterns
    // If user types 3+ digits without decimal (like "120"), automatically add decimal
    if (!cleanValue.includes('.') && cleanValue.length >= 3) {
      // Insert decimal point before last 2 digits (120 -> 1.20)
      cleanValue = cleanValue.slice(0, -2) + '.' + cleanValue.slice(-2);
    }
    
    // Limit to 2 decimal places
    if (cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      if (parts[1] && parts[1].length > 2) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
    
    return cleanValue;
  };

  useEffect(() => {
    if (windowElement) {
      // 🔧 ENHANCED: Populate fields with existing values when editing, empty for new windows
      // Convert from stored mm values to meters for display
      setWidth(windowElement.width ? (windowElement.width / 1000).toFixed(2) : '');
      setHeight(windowElement.height ? (windowElement.height / 1000).toFixed(2) : '');

      setTrickleVents(typeof windowElement.trickleVents === 'number' ? windowElement.trickleVents : 0);
      setIsDraughtProofed(windowElement.isDraughtProofed || false);
      setWindowType(windowElement.windowType || '');
      
      // Initialize orientation settings
      setSelectedOrientation(windowElement.orientation || '');
      
      // Initialize new glazing options
      setGlazingGap((windowElement as any).glazingGap || '');
      setGlazingType((windowElement as any).glazingType || '');
      setWindowCategory((windowElement as any).windowCategory || 'Window');
      setCondition((windowElement as any).condition || '');
      
      console.log('🪟 Window edit dialog populated with existing values:', {
        storedWidthMm: windowElement.width,
        storedHeightMm: windowElement.height,
        displayWidthM: windowElement.width ? (windowElement.width / 1000).toFixed(2) + 'm' : 'N/A',
        displayHeightM: windowElement.height ? (windowElement.height / 1000).toFixed(2) + 'm' : 'N/A',
        trickleVents: windowElement.trickleVents,
        isDraughtProofed: windowElement.isDraughtProofed,
        windowType: windowElement.windowType,
        condition: (windowElement as any).condition,
        orientation: windowElement.orientation,
        glazingGap: (windowElement as any).glazingGap,
        glazingType: (windowElement as any).glazingType,
        windowCategory: (windowElement as any).windowCategory
      });
    }
  }, [windowElement]);

  if (!isOpen || !windowElement) {
    return null;
  }

  const handleSave = () => {
    // Convert from meters input to mm for storage (maintaining compatibility)
    const dimensions = {
      width: (parseFloat(width) || 0) * 1000,
      height: (parseFloat(height) || 0) * 1000,
      trickleVents,
      isDraughtProofed,
      windowType: windowType || undefined,
      orientation: selectedOrientation || undefined,
      glazingGap: glazingGap || undefined,
      glazingType: glazingType || undefined,
      windowCategory: windowCategory || undefined,
      condition: condition || undefined
    };
    
    console.log('🪟 Window dimensions save:', {
      inputMeters: { width, height },
      storedMm: { width: dimensions.width, height: dimensions.height },
      isDraughtProofed,
      windowType,
      condition,
      glazingGap,
      glazingType,
      windowCategory
    });
    
    onSave(dimensions);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
  };

  // Modern dropdown component with icons and enhanced styling
  const ModernDropdown = ({ label, value, options, onChange, placeholder, icon }: {
    label: string;
    value: string;
    options: string[];
    onChange: (value: string) => void;
    placeholder?: string;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer hover:border-slate-300 shadow-sm"
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );

  // Modern counter component for numeric values
  const ModernCounter = ({ label, value, onChange, icon, min = 0, max = 99 }: {
    label: string;
    value: number;
    onChange: (value: number) => void;
    icon?: React.ReactNode;
    min?: number;
    max?: number;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="flex items-center space-x-1.5">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-50 hover:border-slate-400 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
        >
          <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
          </svg>
        </button>
        <div className="flex-1 flex items-center justify-center h-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
          <span className="text-lg font-bold text-blue-900">{value}</span>
        </div>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-blue-400 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:border-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg shadow-blue-500/25"
        >
          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    </div>
  );

  // Modern toggle switch component
  const ModernToggle = ({ label, value, onChange, icon }: {
    label: string;
    value: boolean;
    onChange: (value: boolean) => void;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="relative flex flex-col items-start ml-4">
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-12 items-center justify-center rounded-full border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            value 
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 border-blue-500 shadow-lg shadow-blue-500/25' 
              : 'bg-slate-200 border-slate-300 hover:bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
              value ? 'translate-x-2' : '-translate-x-2'
            }`}
          />
        </button>
        <div className="mt-1">
          <span className={`text-xs font-medium ${value ? 'text-blue-600' : 'text-slate-500'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    </div>
  );

  // Modern button group for frame materials
  const ModernButtonGroup = ({ label, value, options, onChange, icon }: {
    label: string;
    value: string;
    options: Array<{value: string; label: string}>;
    onChange: (value: string) => void;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="flex rounded-lg border-2 border-slate-200 bg-slate-50 p-0.5 transition-all duration-200">
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`flex-1 px-2.5 py-2 text-xs font-medium rounded-md transition-all duration-200 ${
              value === option.value
                ? 'bg-white text-blue-600 shadow-md shadow-blue-500/10 border border-blue-200'
                : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  const frameMaterialOptions = [
    { value: 'pvc', label: 'PVC' },
    { value: 'wood', label: 'Wood' },
    { value: 'metal', label: 'Metal' }
  ];

  const conditionOptions = [
    { value: 'good', label: 'Good' },
    { value: 'average', label: 'Average' },
    { value: 'poor', label: 'Poor' }
  ];

  return (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[1000]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden border border-white/20">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Window Details</h3>
                <p className="text-blue-100 text-xs">Configure specifications</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-white/20 text-white px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm">
                Floor {windowElement?.floor}
              </span>
            </div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/5 to-indigo-600/0 pointer-events-none"></div>
        </div>

        {/* Content Area */}
        <div className="px-5 py-5 space-y-5 max-h-[calc(85vh-140px)] overflow-y-auto">
          {/* Dimensions Section */}
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              Dimensions
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="window-width" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span>Width (m)</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  id="window-width"
                  value={width}
                  onChange={(e) => setWidth(formatDecimalInput(e.target.value))}
                  placeholder="1.20"
                  min="0.1"
                  max="10"
                  step="0.01"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="window-height" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                  </svg>
                  <span>Height (m)</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  id="window-height"
                  value={height}
                  onChange={(e) => setHeight(formatDecimalInput(e.target.value))}
                  placeholder="1.10"
                  min="0.1"
                  max="10"
                  step="0.01"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Window Category */}
          <ModernDropdown
            label="Window Type"
            value={windowCategory}
            options={windowCategoryOptions}
            onChange={setWindowCategory}
            placeholder="Select window type"
            icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>}
          />

          {/* Glazing Information */}
          <div className="space-y-3">
            <ModernDropdown
              label="Glazing Type"
              value={glazingType}
              options={glazingTypeOptions}
              onChange={setGlazingType}
              placeholder="Select glazing type"
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>}
            />
            
            <ModernDropdown
              label="Glazing Gap"
              value={glazingGap}
              options={glazingGapOptions}
              onChange={setGlazingGap}
              placeholder="Select glazing gap"
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>}
            />
          </div>

          {/* Frame Material */}
          <ModernButtonGroup
            label="Frame Material"
            value={windowType}
            options={frameMaterialOptions}
            onChange={(value) => setWindowType(value as 'pvc' | 'wood' | 'metal' | '')}
            icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>}
          />

          {/* Condition */}
          <ModernButtonGroup
            label="Condition"
            value={condition}
            options={conditionOptions}
            onChange={(value) => setCondition(value as 'good' | 'average' | 'poor' | '')}
            icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>}
          />

          {/* Properties */}
          <div className="grid grid-cols-2 gap-3">
            <ModernCounter
              label="Trickle Vents"
              value={trickleVents}
              onChange={setTrickleVents}
              min={0}
              max={20}
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>}
            />
            <ModernToggle
              label="Draught Proofed"
              value={isDraughtProofed}
              onChange={setIsDraughtProofed}
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>}
            />
          </div>

          {/* Orientation Section */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3 border border-slate-200">
            <div className="flex items-center space-x-1.5 mb-3">
              <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 013.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span className="text-xs font-bold text-slate-800">Orientation</span>
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {orientations.map((orientation) => (
                <button
                  key={orientation}
                  type="button"
                  onClick={() => setSelectedOrientation(orientation)}
                  className={`px-2.5 py-2 text-xs font-semibold rounded-lg border-2 transition-all duration-200 ${
                    selectedOrientation === orientation
                      ? 'bg-blue-500 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {orientation}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Modern Action Bar */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex justify-between items-center">
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all duration-200"
            >
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
            <div className="flex space-x-2">
              <button
                onClick={onClose}
                className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WindowDimensionPrompt; 